import { Auth0AccountRole, FormsService, PersonFormRow } from "client/openapi";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import { Dialog } from "components/Dialog";
import { useCallback, useEffect, useState } from "react";
import Notifications from "util/notifications";
import { errorText, sendChosenForm } from "./formsApi";
import ResponseDialog, { formatDate } from "./ResponseDialog";
import {
  ChannelChecks,
  FormPicker,
  audienceFor,
  noFormsText,
  useForms,
} from "./SendFormControls";
import SectionTitle from "components/SectionTitle";
import { BubbleTone } from "components/IconBubble";
import { FileTextIcon } from "@radix-ui/react-icons";
import ViewButton from "./ViewButton";

const STATUS_TEXT: Record<string, string> = {
  [PersonFormRow.status.FILLED_OUT]: "Filled out",
  [PersonFormRow.status.CONFIRMED]: "Filled out", // nothing changed; shown like any other
  [PersonFormRow.status.WAITING]: "Sent, not filled out yet",
  [PersonFormRow.status.EXPIRED]: "Link expired",
};

/**
 * The Forms section of a student's, parent's or tutor's page: the forms sent
 * to them, each filled out one with View to see the answers in a pop-up, and
 * a way to send them a form.
 */
export default function PersonFormsSection({
  orgId,
  personType,
  personId,
}: {
  orgId: number;
  personType: Auth0AccountRole;
  personId: number;
}) {
  const audience = audienceFor(personType);
  const forms = useForms(orgId, audience);
  const [rows, setRows] = useState<PersonFormRow[]>();
  const [choosing, setChoosing] = useState(false);
  const [formId, setFormId] = useState<number | null>(null);
  const [channels, setChannels] = useState({ sms: true, email: false });
  const [sending, setSending] = useState(false);
  const [openResponse, setOpenResponse] = useState<number | null>(null);

  const load = useCallback(() => {
    FormsService.getPersonForms({ orgId, personType, personId })
      .then(setRows)
      .catch((e) =>
        Notifications.error(errorText(e, "Forms could not be loaded."))
      );
  }, [orgId, personType, personId]);

  useEffect(load, [load]);

  function send() {
    if (formId === null) return;
    setSending(true);
    sendChosenForm({ orgId, formId, ...channels, personType, personId })
      .then((allSent) => {
        if (allSent) {
          setFormId(null);
          setChoosing(false);
        }
        load();
      })
      .finally(() => setSending(false));
  }

  const noChannel = !channels.sms && !channels.email;

  return (
    <section className="student-profile--section">
      <div className="student-profile--section-header">
        <SectionTitle icon={<FileTextIcon />} tone={BubbleTone.SKY}>
          Forms
        </SectionTitle>
        {!choosing && (
          <Button
            color={ButtonColor.SKYBLUE}
            size={ButtonSize.SMALL}
            onClick={() => setChoosing(true)}
          >
            Send form
          </Button>
        )}
      </div>
      <div className="student-profile--section-items">
        <div className="student-profile--section-item">
          {choosing && (
            <div className="mb-4">
              <FormPicker
                forms={forms}
                formId={formId}
                onChange={setFormId}
                placeholder="Choose a form to send"
                disabled={sending}
                emptyText={noFormsText(audience)}
              />
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                {formId !== null ? (
                  <ChannelChecks
                    sms={channels.sms}
                    email={channels.email}
                    onChange={setChannels}
                    disabled={sending}
                  />
                ) : (
                  <span />
                )}
                <div className="flex gap-2">
                  <Button
                    color={ButtonColor.GRAY}
                    size={ButtonSize.SMALL}
                    onClick={() => {
                      setChoosing(false);
                      setFormId(null);
                    }}
                    disabled={sending}
                  >
                    Cancel
                  </Button>
                  <Button
                    color={
                      formId === null || noChannel
                        ? ButtonColor.GRAY
                        : ButtonColor.GREEN
                    }
                    size={ButtonSize.SMALL}
                    onClick={send}
                    disabled={sending || formId === null || noChannel}
                  >
                    {sending ? "Sending..." : "Send"}
                  </Button>
                </div>
              </div>
            </div>
          )}
          {rows === undefined && (
            <p className="text-sm text-gray-500">Loading...</p>
          )}
          {rows && rows.length === 0 && (
            <p className="text-sm text-gray-600">No forms sent yet.</p>
          )}
          {rows &&
            rows.map((row, index) => (
              <div
                key={index}
                className="flex w-full items-center justify-between gap-3 border-b border-gray-100 py-2"
              >
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    {row.form_name}
                  </span>
                  <span className="block text-xs text-gray-500">
                    {STATUS_TEXT[row.status]}
                    {row.filled_by ? ` by ${row.filled_by}` : ""}
                  </span>
                </span>
                <span className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {formatDate(row.date)}
                  </span>
                  {/* Every row keeps View's room, so the dates line up. */}
                  <span className="flex w-14 justify-end">
                    {row.response_id && (
                      <ViewButton
                        label={`View ${row.form_name}`}
                        onClick={() =>
                          setOpenResponse(row.response_id as number)
                        }
                      />
                    )}
                  </span>
                </span>
              </div>
            ))}
        </div>
      </div>

      <Dialog
        open={openResponse !== null}
        onOpenChange={(open) => !open && setOpenResponse(null)}
      >
        {openResponse !== null && <ResponseDialog responseId={openResponse} />}
      </Dialog>
    </section>
  );
}
